import React from 'react'
import { useRouter } from 'expo-router'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { events } from '@/models/analytics/analytics-events'
import { useStore } from '@/models'
import { useOnTransitionToTrue } from '@/hooks/useOnTransitionToTrue'
import { StickyDecisionModal } from '@/screens/shared/StickyDecisionModal'
import { useMyTournamentTable } from './useMyTournamentTable'
import { redirectToSummary } from './mttNavigation'
import { useAsyncMutation } from './lobby/useAsyncMutation'

interface Props {
  tournamentId: Id<'tournaments'>
  userId: string
}

/**
 * Tournament reentry modal.
 *
 * Mount once per (tournamentId, userId). Self-gates on the server's
 * `pendingReentry` flag, so it's safe to mount unconditionally for a
 * tournament participant — it renders nothing until the server says
 * a reentry is pending.
 *
 * Decoupled from the HUD on purpose: reentry prompts can fire when the
 * player is between tables / has no `myPlayer` row visible to the game
 * screen. See git history of `TournamentHUD.tsx` for the bug that
 * motivated splitting these.
 */
export function TournamentReentryPrompt({ tournamentId, userId }: Props) {
  const router = useRouter()
  const { setEvent } = useStore().analytics

  const status = useQuery(api.tournaments.queries.getTournamentStatus, { tournamentId })
  const myTable = useMyTournamentTable(tournamentId)
  const reentryMutation = useMutation(api.tournaments.reentry.reenterTournament)
  const declineReentryMutation = useMutation(api.tournaments.reentry.declineTournamentReentry)

  const visible = !!myTable?.pendingReentry

  useOnTransitionToTrue(visible, () => {
    setEvent(events.mtt.reentry_modal_viewed, {
      tournamentId,
      tableNumber: myTable?.tableNumber ?? null
    })
  })

  // Two `useAsyncMutation` calls — one per action key. Each owns its own
  // `pending` flag so the modal can distinguish which button is in-flight.
  // The previous `runAction` helper rolled its own version of this and
  // diverged from `useAsyncMutation`'s toast / stability guarantees
  // (silent `console.error` on failure, recreated identity per render).
  const reentry = useAsyncMutation(
    async () => {
      setEvent(events.mtt.reentry_tapped, { tournamentId })
      await reentryMutation({ tournamentId, userId })
    },
    { errorMessage: 'Re-entry failed. Please try again.' }
  )

  const decline = useAsyncMutation(
    async () => {
      setEvent(events.mtt.leave_tournament_tapped, { tournamentId })
      await declineReentryMutation({ tournamentId, userId })
    },
    {
      errorMessage: 'Could not decline re-entry. Please try again.',
      // On success, route to the bust-out summary (not the leaderboard) so the
      // user gets the "View Leaderboard / Return to Lobby" choices.
      onSuccess: () => redirectToSummary(router, tournamentId)
    }
  )

  if (!(visible && status)) return null

  const pendingKey: null | 'reentry' | 'decline' = reentry.pending ? 'reentry' : decline.pending ? 'decline' : null

  return (
    <StickyDecisionModal
      visible
      title="You've been eliminated"
      body={`Your tournament is still active. Re-enter for ${status.config.startingChips.toLocaleString()} chips to continue playing.`}
      primary={{ label: 'Re-enter Tournament', onPress: () => reentry.run() }}
      secondary={{ label: 'Leave Tournament', onPress: () => decline.run() }}
      pendingKey={pendingKey}
    />
  )
}
